import React, { useState, useEffect } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import { Card } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { Input } from "../components/ui/input";
import { toast } from "sonner";
import { ChevronDown, ChevronUp, Loader2, CheckCircle2 } from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL + "/api";

const FoodPreferenceForm = () => {
  const { formId } = useParams();
  const [searchParams] = useSearchParams();
  const token = searchParams.get("token");

  const [form, setForm] = useState(null);
  const [categories, setCategories] = useState([]);
  const [selected, setSelected] = useState({});
  const [restrictions, setRestrictions] = useState("");
  const [religious, setReligious] = useState("");
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [expandedCat, setExpandedCat] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchForm = async () => {
      try {
        const res = await fetch(`${API}/public/food-preference-form/${formId}?token=${token}`);
        if (res.ok) {
          const data = await res.json();
          setForm(data.form);
          setCategories(data.categories || []);
          if (data.form.selected_foods && Object.keys(data.form.selected_foods).length > 0) {
            setSelected(data.form.selected_foods);
          }
          if (data.form.dietary_restrictions) setRestrictions(data.form.dietary_restrictions);
          if (data.form.religious_considerations) setReligious(data.form.religious_considerations);
          if (data.form.status === "completed") setSubmitted(true);
        } else {
          setError("This form link is invalid or has expired.");
        }
      } catch { setError("Could not load the form. Please try again."); }
      finally { setLoading(false); }
    };
    if (formId && token) fetchForm();
    else { setError("Invalid form link."); setLoading(false); }
  }, [formId, token]);

  const toggleFood = (catName, food) => {
    setSelected(prev => {
      const catFoods = prev[catName] || [];
      const next = catFoods.includes(food) ? catFoods.filter(f => f !== food) : [...catFoods, food];
      return { ...prev, [catName]: next };
    });
  };

  const handleSubmit = async () => {
    setSubmitting(true);
    try {
      const res = await fetch(`${API}/public/food-preference-form/${formId}?token=${token}`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ selected_foods: selected, dietary_restrictions: restrictions, religious_considerations: religious }),
      });
      if (res.ok) {
        setSubmitted(true);
        toast.success("Your food preferences have been saved.");
      } else {
        toast.error("Could not save. Please try again.");
      }
    } catch { toast.error("Connection error. Please try again."); }
    finally { setSubmitting(false); }
  };

  const totalSelected = Object.values(selected).reduce((sum, arr) => sum + (arr?.length || 0), 0);

  if (loading) return (
    <div className="min-h-screen bg-[#FFFDF8] flex items-center justify-center">
      <Loader2 className="w-8 h-8 animate-spin text-[#FF7A00]" />
    </div>
  );

  if (error) return (
    <div className="min-h-screen bg-[#FFFDF8] flex items-center justify-center p-6">
      <Card className="max-w-md w-full p-8 text-center rounded-2xl">
        <p className="text-base text-stone-600">{error}</p>
      </Card>
    </div>
  );

  if (submitted) return (
    <div className="min-h-screen bg-[#FFFDF8] flex items-center justify-center p-6">
      <Card className="max-w-md w-full p-8 text-center rounded-2xl">
        <CheckCircle2 className="w-12 h-12 text-green-500 mx-auto mb-4" />
        <h1 className="text-xl font-bold text-stone-900 mb-2">Thank you!</h1>
        <p className="text-sm text-stone-500">Your food preferences have been saved. Your dietitian will use them to create a personalised meal plan for you.</p>
      </Card>
    </div>
  );

  return (
    <div className="min-h-screen bg-[#FFFDF8]" data-testid="food-pref-form">
      <div className="max-w-[720px] mx-auto px-5 py-8 sm:py-12">
        {/* Header */}
        <div className="flex items-center gap-3 mb-6">
          <img src="/platemind-logo-new.png" alt="PlateMind" className="h-8 w-auto" />
        </div>

        {form?.patient_name && (
          <p className="text-sm text-stone-500 mb-2">For {form.patient_name}</p>
        )}

        <h1 className="text-2xl sm:text-3xl font-extrabold text-[#1C1C1C] tracking-tight mb-2" data-testid="form-title">
          Your food preferences
        </h1>
        <p className="text-base text-stone-500 mb-8">
          Tick the foods and drinks you enjoy or would like in your meal plan. Pick as many as apply.
        </p>

        {/* Food categories */}
        <div className="space-y-2 mb-8">
          {categories.map(cat => {
            const catSelected = selected[cat.name] || [];
            const isExpanded = expandedCat === cat.name;
            return (
              <div key={cat.category_id} className="bg-white border border-stone-200 rounded-xl overflow-hidden">
                <button
                  onClick={() => setExpandedCat(isExpanded ? null : cat.name)}
                  className="w-full flex items-center justify-between px-5 py-3.5 text-left hover:bg-stone-50/50 transition-colors"
                >
                  <div className="flex items-center gap-2">
                    <span className="text-sm font-semibold text-stone-800">{cat.name}</span>
                    {catSelected.length > 0 && (
                      <Badge className="bg-[#FF7A00]/10 text-[#FF7A00] border-[#FF7A00]/20 text-[10px] px-1.5">{catSelected.length}</Badge>
                    )}
                  </div>
                  {isExpanded ? <ChevronUp className="w-4 h-4 text-stone-400" /> : <ChevronDown className="w-4 h-4 text-stone-400" />}
                </button>
                {isExpanded && (
                  <div className="px-5 pb-4 pt-1 border-t border-stone-100">
                    <div className="flex flex-wrap gap-2">
                      {(cat.foods || []).map(food => {
                        const isSelected = catSelected.includes(food);
                        return (
                          <button
                            key={food}
                            onClick={() => toggleFood(cat.name, food)}
                            className={`px-3 py-1.5 rounded-full text-sm font-medium border transition-all ${
                              isSelected
                                ? "bg-[#FF7A00] border-[#FF7A00] text-white"
                                : "bg-white border-stone-200 text-stone-600 hover:border-stone-300 hover:bg-stone-50"
                            }`}
                          >
                            {food}
                          </button>
                        );
                      })}
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>

        {/* Additional fields */}
        <div className="space-y-4 mb-8">
          <div>
            <label className="block text-sm font-medium text-stone-700 mb-1.5">Dietary restrictions</label>
            <Input placeholder="e.g. Vegetarian, halal, lactose-free..." value={restrictions} onChange={e => setRestrictions(e.target.value)} className="rounded-lg bg-white" />
          </div>
          <div>
            <label className="block text-sm font-medium text-stone-700 mb-1.5">Religious or cultural considerations</label>
            <Input placeholder="e.g. Fasting periods, etc." value={religious} onChange={e => setReligious(e.target.value)} className="rounded-lg bg-white" />
          </div>
        </div>

        {/* Submit */}
        <div className="flex items-center justify-between">
          <p className="text-sm text-stone-400">{totalSelected} food{totalSelected !== 1 ? "s" : ""} selected</p>
          <Button
            onClick={handleSubmit}
            disabled={submitting}
            className="bg-[#FF7A00] hover:bg-[#E56E00] text-white rounded-full px-8 h-12 text-base font-semibold"
            data-testid="submit-food-prefs"
          >
            {submitting ? <Loader2 className="w-5 h-5 animate-spin mr-2" /> : null}
            Save my preferences
          </Button>
        </div>
      </div>
    </div>
  );
};

export default FoodPreferenceForm;
